// ==========================================
// NAVBAR — Mission Control
// ==========================================

import { Link, useNavigate, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useState, useEffect, useRef } from 'react';
import { useAuthStore } from '../stores/authStore';
import { useOnlineStore } from '../stores/onlineStore';
import { useSocketStatusStore } from '../stores/socketStore';
import { motion, AnimatePresence } from 'framer-motion';
import { FEATURES } from '../config/features';

export default function Navbar() {
  const { t, i18n } = useTranslation();
  const toggleLang = () => i18n.changeLanguage(i18n.language.startsWith('fr') ? 'en' : 'fr');
  const navigate = useNavigate();
  const { user, isAuthenticated, logout } = useAuthStore();
  const { onlineCount, activeGamesCount } = useOnlineStore();
  const socketStatus = useSocketStatusStore(s => s.status);
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isProfileOpen, setIsProfileOpen] = useState(false);
  const profileRef = useRef<HTMLDivElement>(null);
  const mobileMenuRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (profileRef.current && !profileRef.current.contains(event.target as Node)) {
        setIsProfileOpen(false);
      }
      if (mobileMenuRef.current && !mobileMenuRef.current.contains(event.target as Node)) {
        setIsMenuOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleLogout = () => {
    logout();
    navigate('/');
  };

  return (
    <nav className="fixed top-0 left-0 right-0 z-50 backdrop-blur-md" style={{ background: 'rgba(5,5,5,0.92)', borderBottom: '1px solid rgba(255,255,255,0.04)' }}>
      <div className="max-w-6xl mx-auto px-4">
        <div className="flex items-center justify-between h-14">
          {/* Logo */}
          <Link to="/" className="flex items-center gap-2.5">
            <svg width="24" height="24" viewBox="0 0 24 24" fill="#555">
              <path d="M21 6H3c-1.1 0-2 .9-2 2v8c0 1.1.9 2 2 2h18c1.1 0 2-.9 2-2V8c0-1.1-.9-2-2-2zM11 13H8v3H6v-3H3v-2h3V8h2v3h3v2zm4.5 2c-.83 0-1.5-.67-1.5-1.5s.67-1.5 1.5-1.5 1.5.67 1.5 1.5-.67 1.5-1.5 1.5zm4-3c-.83 0-1.5-.67-1.5-1.5S18.67 9 19.5 9s1.5.67 1.5 1.5-.67 1.5-1.5 1.5z"/>
            </svg>
            <span className="font-arcade text-[0.65rem] text-white hidden sm:block tracking-wider">
              QPUG
            </span>
          </Link>

          {/* Online count — minimal */}
          <div className="hidden sm:flex items-center gap-2 px-2.5 py-1 rounded-lg" style={{ background: 'rgba(255,255,255,0.02)', border: '1px solid rgba(255,255,255,0.04)' }}>
            {/* Socket status dot */}
            <span
              className={`inline-block w-1.5 h-1.5 rounded-full ${
                socketStatus === 'connected' ? 'bg-green-500' :
                socketStatus === 'reconnecting' ? 'bg-yellow-500 animate-pulse' :
                'bg-red-500'
              }`}
              title={socketStatus === 'connected' ? 'Connected' : socketStatus === 'reconnecting' ? 'Reconnecting...' : 'Disconnected'}
            />
            <span className="font-mono text-[0.65rem] text-[#555]">
              {onlineCount}
            </span>
            <span className="text-[0.55rem] text-[#333] hidden lg:block font-mono uppercase tracking-wider">
              {t('nav.online', 'EN LIGNE')}
            </span>
          </div>

          {/* Desktop nav */}
          <div className="hidden md:flex items-center gap-5">
            <Link
              to="/"
              className="text-[#555] hover:text-[#888] transition-colors text-sm"
            >
              {t('nav.home')}
            </Link>
            <Link
              to="/games"
              className="text-[#555] hover:text-[#888] transition-colors text-sm flex items-center gap-1.5"
            >
              {t('nav.games')}
              {activeGamesCount > 0 && (
                <span className="text-[0.6rem] font-mono px-1.5 py-0.5 rounded-full text-[#FF6B00]" style={{ background: 'rgba(255,107,0,0.1)' }}>
                  {activeGamesCount}
                </span>
              )}
            </Link>
            <Link
              to="/leaderboard"
              className="text-[#555] hover:text-[#888] transition-colors text-sm"
            >
              {t('nav.leaderboard')}
            </Link>
            <button
              onClick={() => {
                if (!isAuthenticated) {
                  window.dispatchEvent(new CustomEvent('open:loginRequired', { detail: { message: 'Connecte-toi pour accéder aux défis quotidiens et hebdomadaires !' } }));
                  return;
                }
                const loc = window.location.pathname;
                if (loc === '/' || loc === '') {
                  window.dispatchEvent(new Event('open:challenges'));
                } else {
                  navigate('/');
                  setTimeout(() => window.dispatchEvent(new Event('open:challenges')), 300);
                }
              }}
              className="text-sm font-medium transition-colors flex items-center gap-1"
              style={{ color: '#00D4FF' }}
            >
              🎯 Défis
            </button>
            <Link
              to="/premium"
              className="text-sm font-medium transition-colors flex items-center gap-1"
              style={{ color: '#FF6B00' }}
            >
              👑 Premium
            </Link>

            {/* Language toggle */}
            <button
              onClick={toggleLang}
              className="text-[0.65rem] font-mono px-2 py-1 rounded-md text-[#444] hover:text-white transition-colors"
              style={{ border: '1px solid rgba(255,255,255,0.06)' }}
              title={t('settings.language')}
            >
              {i18n.language.startsWith('fr') ? 'EN' : 'FR'}
            </button>

            {isAuthenticated ? (
              <>
                <Link
                  to="/lobby"
                  className="btn btn-primary text-xs py-2 px-4"
                >
                  {t('nav.play')}
                </Link>

                {/* Profile menu */}
                <div className="relative" ref={profileRef}>
                  <button
                    onClick={() => setIsProfileOpen(!isProfileOpen)}
                    className="flex items-center gap-2 text-[#555] hover:text-white transition-colors"
                  >
                    <div
                      className="w-7 h-7 rounded-full flex items-center justify-center text-xs font-bold"
                      style={{ background: '#161616', color: '#555', border: '1px solid rgba(255,255,255,0.06)' }}
                    >
                      {user?.pseudo?.charAt(0).toUpperCase()}
                    </div>
                    <span className="hidden lg:block text-sm text-[#888]">{user?.pseudo}</span>
                    <svg className={`w-3 h-3 transition-transform text-[#444] ${isProfileOpen ? 'rotate-180' : ''}`} fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                    </svg>
                  </button>

                  <AnimatePresence>
                    {isProfileOpen && (
                      <motion.div
                        initial={{ opacity: 0, y: -5 }}
                        animate={{ opacity: 1, y: 0 }}
                        exit={{ opacity: 0, y: -5 }}
                        className="absolute right-0 mt-2 w-44 rounded-xl overflow-hidden"
                        style={{ background: '#0d0d0d', border: '1px solid rgba(255,255,255,0.06)' }}
                      >
                        <Link
                          to="/profile"
                          className="block px-4 py-2.5 text-sm text-[#555] hover:text-white hover:bg-white/[0.03] transition-colors"
                          onClick={() => setIsProfileOpen(false)}
                        >
                          {t('nav.profile')}
                        </Link>
                        <Link
                          to="/settings"
                          className="block px-4 py-2.5 text-sm text-[#555] hover:text-white hover:bg-white/[0.03] transition-colors"
                          onClick={() => setIsProfileOpen(false)}
                        >
                          {t('nav.settings')}
                        </Link>
                        <div style={{ borderTop: '1px solid rgba(255,255,255,0.04)' }} />
                        <button
                          onClick={handleLogout}
                          className="w-full text-left px-4 py-2.5 text-sm text-red-400/60 hover:text-red-400 hover:bg-white/[0.03] transition-colors"
                        >
                          {t('nav.logout')}
                        </button>
                      </motion.div>
                    )}
                  </AnimatePresence>
                </div>
              </>
            ) : (
              <div className="flex items-center gap-3">
                <Link
                  to="/login"
                  className="text-[#555] hover:text-white transition-colors text-sm"
                >
                  {t('nav.login')}
                </Link>
                <Link
                  to="/register"
                  className="btn btn-primary text-xs py-2 px-4"
                >
                  {t('nav.register')}
                </Link>
              </div>
            )}
          </div>

          {/* Mobile menu toggle */}
          <button
            onClick={() => setIsMenuOpen(!isMenuOpen)}
            className="md:hidden p-2 text-[#555] hover:text-white transition-colors"
          >
            <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              {isMenuOpen ? (
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              ) : (
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
              )}
            </svg>
          </button>
        </div>

        {/* Mobile menu */}
        <AnimatePresence>
          {isMenuOpen && (
            <motion.div
              initial={{ opacity: 0, height: 0 }}
              animate={{ opacity: 1, height: 'auto' }}
              exit={{ opacity: 0, height: 0 }}
              className="md:hidden"
              style={{ borderTop: '1px solid rgba(255,255,255,0.04)' }}
            >
              <div className="py-4 space-y-1">
                <Link
                  to="/"
                  className="block py-2 px-2 text-sm text-[#555] hover:text-white transition-colors rounded-lg hover:bg-white/[0.02]"
                  onClick={() => setIsMenuOpen(false)}
                >
                  {t('nav.home')}
                </Link>
                <Link
                  to="/games"
                  className="block py-2 px-2 text-sm text-[#555] hover:text-white transition-colors rounded-lg hover:bg-white/[0.02]"
                  onClick={() => setIsMenuOpen(false)}
                >
                  {t('nav.games')}
                </Link>
                <Link
                  to="/leaderboard"
                  className="block py-2 px-2 text-sm text-[#555] hover:text-white transition-colors rounded-lg hover:bg-white/[0.02]"
                  onClick={() => setIsMenuOpen(false)}
                >
                  {t('nav.leaderboard')}
                </Link>

                {isAuthenticated ? (
                  <>
                    <Link
                      to="/lobby"
                      className="block py-2 px-2 text-sm text-[#FF6B00] transition-colors rounded-lg hover:bg-white/[0.02]"
                      onClick={() => setIsMenuOpen(false)}
                    >
                      {t('nav.play')}
                    </Link>
                    <Link
                      to="/profile"
                      className="block py-2 px-2 text-sm text-[#555] hover:text-white transition-colors rounded-lg hover:bg-white/[0.02]"
                      onClick={() => setIsMenuOpen(false)}
                    >
                      {t('nav.profile')}
                    </Link>
                    <button
                      onClick={() => {
                        handleLogout();
                        setIsMenuOpen(false);
                      }}
                      className="block py-2 px-2 text-sm text-red-400/60 hover:text-red-400 transition-colors w-full text-left rounded-lg hover:bg-white/[0.02]"
                    >
                      {t('nav.logout')}
                    </button>
                  </>
                ) : (
                  <>
                    <Link
                      to="/login"
                      className="block py-2 px-2 text-sm text-[#555] hover:text-white transition-colors rounded-lg hover:bg-white/[0.02]"
                      onClick={() => setIsMenuOpen(false)}
                    >
                      {t('nav.login')}
                    </Link>
                    <Link
                      to="/register"
                      className="block py-2 px-2 text-sm text-[#FF6B00] transition-colors rounded-lg hover:bg-white/[0.02]"
                      onClick={() => setIsMenuOpen(false)}
                    >
                      {t('nav.register')}
                    </Link>
                  </>
                )}
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </nav>
  );
}
